import clipping from 'polygon-clipping';
import { clipWithPrecisionRetry } from './precisionClipping.js';
import { WALL_THICKNESS } from './constants.js';
import { stairHoles } from './stairs.js';
import { orderedSiteAreas, siteSurfaces } from './siteAreas.js';

export const SLAB_DEPTH = 0.16;
export const ROOF_DEPTH = 0.2;
const EPS = 1e-5;
const ceilingCache = new WeakMap(), slabCache = new WeakMap();
const structureKey = S => JSON.stringify([S.areas, S.walls, S.elevation, S.stairs, S._stairLevels,
  S.floors?.map(f => [f.id, f.elevation])]);
const ring = pts => pts.map(p => [p.x, p.y]);
export const prismPolygons = w => [[ring(w.roofPts), ...(w.roofHoles || []).map(ring)]];
export const polygonPrism = (rings, props) => {
  const pts = rings[0].map(([x, y]) => ({ x, y }));
  return { ...props, roofPts: pts, roofHoles: rings.slice(1).map(r => r.map(([x, y]) => ({ x, y }))),
    x1: Math.min(...pts.map(p => p.x)), y1: Math.min(...pts.map(p => p.y)),
    x2: Math.max(...pts.map(p => p.x)), y2: Math.max(...pts.map(p => p.y)) };
};

/** Floor outlines describe wall centrelines. Carry structural caps to the
 * outside face only where a real wall follows that outline. Unwalled terraces,
 * courtyards and stair apertures keep their original boundaries. */
export function capFootprint(polygons, walls) {
  const strips = [];
  for (const rings of polygons) for (const boundary of rings) for (let i = 0; i < boundary.length; i++) {
    const a = boundary[i], b = boundary[(i + 1) % boundary.length], dx = b[0] - a[0], dy = b[1] - a[1], length = Math.hypot(dx, dy);
    if (length < EPS) continue;
    const ux = dx / length, uy = dy / length;
    for (const w of walls || []) {
      if (Math.abs((w.x1 - a[0]) * uy - (w.y1 - a[1]) * ux) > EPS || Math.abs((w.x2 - a[0]) * uy - (w.y2 - a[1]) * ux) > EPS) continue;
      const p = (w.x1 - a[0]) * ux + (w.y1 - a[1]) * uy, q = (w.x2 - a[0]) * ux + (w.y2 - a[1]) * uy;
      const lo = Math.max(0, Math.min(p, q)), hi = Math.min(length, Math.max(p, q));
      if (hi - lo < EPS) continue;
      const t = (w.thickness ?? WALL_THICKNESS) / 2;
      // Square joins cover the outer corner shared by perpendicular walls.
      strips.push([[[lo-t,-t],[hi+t,-t],[hi+t,t],[lo-t,t]].map(([u,v]) => [a[0]+ux*u-uy*v,a[1]+uy*u+ux*v])]);
    }
  }
  return strips.length ? clipWithPrecisionRetry('union', polygons, ...strips) : polygons;
}

/** Merge coplanar ceilings before extrusion, avoiding duplicate faces over
 * nested room/shell areas. Smaller room definitions retain their own height. */
export function ceilingPrisms(S) {
  if (!S.areas?.length) return [];
  const key = structureKey(S), previous = ceilingCache.get(S.areas);
  if (previous?.key === key) return previous.value;
  const groups = new Map(); let covered = [];
  for (const a of orderedSiteAreas(S).reverse()) {
    if (a.environment === 'outdoor' || !(a.roofHeight > 0)) continue;
    const shape = [ring(a.pts)], polygons = covered.length ? clipping.difference(shape, covered) : [shape];
    covered = clipping.union(covered, shape);
    const group = groups.get(a.roofHeight) || { areas: [], polygons: [] };
    group.areas.push(a); group.polygons.push(...polygons); groups.set(a.roofHeight, group);
  }
  const out = [];
  for (const [height, { areas, polygons }] of groups) {
    if (!polygons.length) continue;
    let caps = capFootprint(clipping.union(polygons), (S.walls || []).filter(w => w.h >= height - EPS));
    const holes = stairHoles(S, (S.elevation || 0) + height).map(pts => [ring(pts)]);
    if (holes.length) caps = clipping.difference(caps, ...holes);
    caps.forEach((r, i) => out.push(polygonPrism(r, { id: areas[0].id + ':roof:' + i, sourceId: areas[0].id,
      sourceIds: areas.map(a => a.id), sources: areas.map(a => ({ id: a.id, pts: a.pts })),
      name: areas[0].name + ' roof / ceiling', objectKind: 'roof', kind: 'solid',
      mountRule: 'prohibited', bottom: height, h: height + ROOF_DEPTH, thickness: 0 })));
  }
  ceilingCache.set(S.areas, { key, value: out }); return out;
}

/** Close the ordinary service void above a modeled ceiling up to the next
 * structural slab. Work only under overlapping upper floors; keep terrace
 * roofs, stair holes and deliberate tall/open gaps. Heights stay floor-local. */
export function fitCeilingToFloors(roof, floors) {
  let remaining = prismPolygons(roof); const out = [];
  for (const f of floors.slice().sort((a,b) => a.elevation-b.elevation)) {
    const underside = f.elevation - SLAB_DEPTH;
    if (!f.polygons.length || f.elevation < roof.bottom - EPS || underside > roof.h + 1) continue;
    const overlap = clipping.intersection(remaining, f.polygons);
    if (!overlap.length) continue;
    remaining = clipping.difference(remaining, f.polygons);
    // When the upper slab already covers the ceiling, it replaces that part.
    if (underside > roof.bottom + EPS) out.push(...overlap.map(r => polygonPrism(r, { ...roof, h: underside })));
  }
  return out.concat(remaining.map(r => polygonPrism(r, roof)));
}

export function structuralSlabs(S) {
  if (!S.areas?.length) return [];
  const key = structureKey(S), previous = slabCache.get(S.areas);
  if (previous?.key === key) return previous.value;
  // Coverage exclusions do not punch holes through physical construction.
  const layers = siteSurfaces({ ...S, areas: S.areas.filter(a => a.kind !== 'exclude') }).layers.filter(l => l.area.environment !== 'outdoor');
  if (!layers.length) return [];
  let polygons = capFootprint(clipping.union(...layers.map(l => l.polygons)), S.walls);
  const holes = stairHoles(S).map(pts => [ring(pts)]);
  if (holes.length) polygons = clipping.difference(polygons, ...holes);
  const value = polygons.map(r => ({ rings: r, area: layers[0].area, areaIds: layers.map(l => l.area.id),
    sources: layers.map(l => ({ id: l.area.id, pts: l.area.pts })) }));
  slabCache.set(S.areas, { key, value }); return value;
}
